import { useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { HelpCircle, Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FaqItem } from './FaqItem';
import { containerVariants } from './motion';
import type { FaqEntry } from '../mockKnowledge';

interface FaqSectionProps {
  faqs: FaqEntry[];
  onAdd: (entry: Pick<FaqEntry, 'question' | 'answer'>) => void;
  onUpdate: (id: string, updates: Partial<FaqEntry>) => void;
  onDelete: (id: string) => void;
  onToggleInKnowledgeBase: (id: string) => void;
}

export function FaqSection({ faqs, onAdd, onUpdate, onDelete, onToggleInKnowledgeBase }: FaqSectionProps) {
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [policyTitle, setPolicyTitle] = useState('');
  const [policyText, setPolicyText] = useState('');

  const handleAddFaq = (event: FormEvent) => {
    event.preventDefault();
    if (!newFaq.question.trim()) return;
    onAdd({ question: newFaq.question.trim(), answer: newFaq.answer.trim() });
    setNewFaq({ question: '', answer: '' });
    toast.success('Added to AI knowledge');
  };

  const handleAddPolicy = (event: FormEvent) => {
    event.preventDefault();
    if (!policyText.trim()) return;
    onAdd({ question: policyTitle.trim() || 'Policy Document', answer: policyText.trim() });
    setPolicyTitle('');
    setPolicyText('');
    toast.success('Policy document added to AI knowledge');
  };

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
      {/* Pod Header */}
      <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5 gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
            <HelpCircle className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            FAQ & Policy Guidelines
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-800/80 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-400">
            {faqs.length} Entries
          </span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Auto-answers customer queries</span>
      </div>

      <div className="p-4 bg-white/70 dark:bg-slate-950/40 space-y-4">
        {/* Quick Add Form */}
        <form onSubmit={handleAddFaq} className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={newFaq.question}
            onChange={(event) => setNewFaq((prev) => ({ ...prev, question: event.target.value }))}
            placeholder="Question or topic (e.g. Delivery charge?)"
            aria-label="New FAQ question or topic"
            className="h-8.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 sm:w-1/3 focus:border-primary/40 focus:ring-primary/20 shadow-2xs"
          />
          <Input
            value={newFaq.answer}
            onChange={(event) => setNewFaq((prev) => ({ ...prev, answer: event.target.value }))}
            placeholder="Direct answer the AI should deliver"
            aria-label="New FAQ answer"
            className="h-8.5 flex-1 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary/40 focus:ring-primary/20 shadow-2xs"
          />
          <Button type="submit" size="sm" className="h-8.5 px-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs shrink-0 transition-colors">
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add FAQ
          </Button>
        </form>

        {/* FAQ items list */}
        <motion.ul variants={containerVariants} initial="hidden" animate="visible" className="space-y-2">
          <AnimatePresence>
            {faqs.map((faq) => (
              <FaqItem
                key={faq.id}
                faq={faq}
                onUpdate={(updates) => onUpdate(faq.id, updates)}
                onDelete={() => onDelete(faq.id)}
                onToggleInKnowledgeBase={() => onToggleInKnowledgeBase(faq.id)}
              />
            ))}
          </AnimatePresence>
        </motion.ul>

        {faqs.length === 0 && <p className="py-4 text-center text-xs text-slate-400 dark:text-slate-500">No FAQ or policy entries added yet.</p>}

        {/* Policy document paste area */}
        <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/60 p-3.5 space-y-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
            <FileText className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            Paste Policy Document
          </div>
          <form onSubmit={handleAddPolicy} className="space-y-2">
            <Input
              id="policy-title"
              value={policyTitle}
              onChange={(event) => setPolicyTitle(event.target.value)}
              placeholder="Document name (e.g. Return & Exchange Policy)"
              className="h-8 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary/40 focus:ring-primary/20 shadow-2xs"
            />
            <Textarea
              value={policyText}
              onChange={(event) => setPolicyText(event.target.value)}
              placeholder="Paste full terms, return windows, warranty or refund rules here…"
              rows={3}
              aria-label="Policy document text"
              className="resize-y rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary/40 focus:ring-primary/20 shadow-2xs leading-relaxed"
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" variant="outline" className="h-8 px-4 rounded-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 shadow-2xs">
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add Policy Document
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Card>
  );
}

export default FaqSection;
